import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthShell from "@/components/AuthShell";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: "operator", title: "Postal Operator", desc: "Float and manage tenders, evaluate bids, award contracts." },
  { value: "vendor", title: "Vendor / Bidder", desc: "Browse global tenders, ask clarifications, submit bids." },
];

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "operator", organization: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/register`, form);
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Create account" subtitle="Join the global postal procurement network.">
      <form onSubmit={submit} className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          {ROLE_OPTIONS.map((r) => (
            <button
              type="button"
              key={r.value}
              data-testid={`register-role-${r.value}`}
              onClick={() => set("role")(r.value)}
              className={`text-left p-4 border transition-colors duration-200 ${form.role === r.value ? "border-[#0050A0] bg-[#E6F0F9]" : "border-slate-200 bg-white hover:border-slate-400"}`}
            >
              <p className="text-sm font-semibold text-slate-900">{r.title}</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{r.desc}</p>
            </button>
          ))}
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Full Name</Label>
          <Input data-testid="register-name-input" required className="bg-white border-slate-300" value={form.name} onChange={(e) => set("name")(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Organization</Label>
          <Input data-testid="register-organization-input" placeholder={form.role === "operator" ? "e.g., India Post" : "e.g., EVMotors Global"} className="bg-white border-slate-300" value={form.organization} onChange={(e) => set("organization")(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Email</Label>
          <Input data-testid="register-email-input" type="email" required className="bg-white border-slate-300" value={form.email} onChange={(e) => set("email")(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Password</Label>
          <Input data-testid="register-password-input" type="password" required minLength={6} className="bg-white border-slate-300" value={form.password} onChange={(e) => set("password")(e.target.value)} />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="register-submit-btn" className="w-full bg-[#0050A0] text-white py-3.5 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200 disabled:opacity-60">
          {busy ? "Creating…" : "Create Account"}
        </button>
        <p className="text-sm text-slate-500 text-center">
          Already registered?{" "}
          <Link to="/login" data-testid="register-goto-login-link" className="text-[#0050A0] font-medium hover:text-[#009EDB] transition-colors duration-200">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  );
}
